import { Component, Inject, OnInit, ViewChildren, ViewChild, ElementRef } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { Employment, Unemployment } from 'src/app/shared/models/employment.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { DatePipe } from '@angular/common';
import { Location } from '@angular/common';
import dayjs from 'dayjs';

import { ToastService } from 'src/app/shared/toast/toast.service';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { Driver } from 'src/app/shared/models/driver.model';
import { of } from 'rxjs';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { compareEmploymentByToDateDesc } from 'src/app/shared/utils/employment-sort.util';
import {
  CROSS_STATE_OVERLAP_WARNING_MESSAGE,
  EMPLOYMENT_STATE_UNEMPLOYED,
  hasOppositeStateDateOverlap
} from 'src/app/shared/utils/employment-overlap.util';
import { ROUTE_EMPLOYMENT_HISTORY } from 'src/app/shared/routes';

const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const EARLIEST_UNEMPLOYMENT_YEAR = 1900;

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY'
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
@Component({
    selector: 'app-employment-history-form-unemployed',
    templateUrl: './employment-history-form-unemployed.component.html',
    styleUrls: [
        './employment-history-form-unemployed.component.scss',
        '../employment-history-form-employed/employment-history-form-employed.component.scss'
    ],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        DatePipe
    ],
    standalone: false,
    host: {
        '[class.show-nav]': 'showNavBar'
    }
})
export class EmploymentHistoryFormUnemployedComponent implements OnInit {
  employmentList = [];
  isFirstRecord: boolean;
  unEmpForm: UntypedFormGroup;
  errorMessage: string;
  overlapWarningMessage: string;
  isUpdating: boolean;
  currentDate = new Date();
  lastDate: Date;
  isGreaterThan10years: boolean = false;
  editFormTitle: string = 'Unemployment';
  minFromDate;
  maxToDate: Date;
  isContentReady: boolean = false;
  showNavBar: boolean = true; // Controls bottom navigation visibility
  isModifyNewCurrentAddress: boolean;
  driver: Driver;
  currentNewUnEmployment;

  @ViewChildren('unEmpFromDatePicker') unEmpFromDateList;
  @ViewChildren('unEmpToDatePicker') unEmpToDateList;
  @ViewChild('datePickerSection', { static: false }) datePickerSection: ElementRef;
  private subs = new SubSink();

  // Month and year options for dropdowns
  months = [
    { value: 0, name: 'January' },
    { value: 1, name: 'February' },
    { value: 2, name: 'March' },
    { value: 3, name: 'April' },
    { value: 4, name: 'May' },
    { value: 5, name: 'June' },
    { value: 6, name: 'July' },
    { value: 7, name: 'August' },
    { value: 8, name: 'September' },
    { value: 9, name: 'October' },
    { value: 10, name: 'November' },
    { value: 11, name: 'December' }
  ];

  // Generate years from 30 years ago to current year
  years: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const startYear = EARLIEST_UNEMPLOYMENT_YEAR;
    const yearsArray: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
      yearsArray.push(year);
    }
    return yearsArray;
  })();

  // Cached properties for dropdowns (computed once and cached)
  availableFromYears: number[] = [];
  availableFromMonths: any[] = [];
  availableToYears: number[] = [];
  availableToMonths: any[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly toastService: ToastService,
    private readonly storageService: StorageService,
    private readonly datePipe: DatePipe,
    @Inject('environmentData') public environment: any
  ) {
    this.errorMessage = undefined;
    this.unEmpForm = this.createUnEmploymentForm();
    this.initializeDates();
  }

  ngOnInit(): void {
    this.driver = this.registrationService.getDriver();
    this.currentNewUnEmployment = this.storageService.find('currentEmploymentState');
    this.loadExperience();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onAddUnemployment(isMissingIndex?: number, gapPrevFromDate?: string, gapNextToDate?: string): void {
    // IMPORTANT: Set minFromDate and maxToDate BEFORE creating the form
    // so the year/month filters work correctly from the start
    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const prevEmp = this.employmentList[isMissingIndex - 1];
      const prevFrom = gapPrevFromDate ?? prevEmp?.fromDate;
      const nextTo = gapNextToDate ?? nextEmp?.toDate;
      
      if (nextTo && prevFrom) {
        this.minFromDate = dayjs(nextTo).toDate();
        this.maxToDate = dayjs(prevFrom).toDate();
      } else {
        // Fallback if dates are missing
        this.minFromDate = null;
        this.maxToDate = new Date(this.currentDate);
      }
    } else {
      // Reset for non-gap scenarios
      this.minFromDate = null;
      this.maxToDate = new Date(this.currentDate);
    }
    
    this.unEmpForm = this.createUnEmploymentForm();
    this.editFormTitle = 'Unemployment';

    // Check if this is for the first record or a new current unemployment
    if (this.isFirstRecord || this.isModifyNewCurrentAddress) {
      this.editFormTitle = 'Are you currently not working?';
      this.unEmpToDate.setValue(this.currentDate);
      this.unEmpForm.get('isCurrentEmployment').setValue(true);
      
      // Set default month/year for current unemployment
      const currentMonth = this.currentDate.getMonth();
      const currentYear = this.currentDate.getFullYear();
      this.unEmpForm.patchValue({
        toMonth: currentMonth,
        toYear: currentYear
      });
      // Clear validators for TO date fields for current unemployment
      this.unEmpForm.get('toMonth')?.clearValidators();
      this.unEmpForm.get('toYear')?.clearValidators();
      this.unEmpForm.get('toMonth')?.updateValueAndValidity();
      this.unEmpForm.get('toYear')?.updateValueAndValidity();
    } else {
      // This is for a previous unemployment
      this.editFormTitle = 'Were you not working during this time?';

      // Check if there's a current employment record
      const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);

      if (!hasCurrentEmployment && (isMissingIndex === undefined || isMissingIndex === null)) {
        // Block adding previous unemployment without a current employment
        this.toastService.showError('You need to add your current employment first.');
        this.location.back();
        return;
      }

      // Set appropriate dates - prefill based on the most recent employment
      const lastEmployment = this.employmentList[this.employmentList.length - 1];
      const toDateDayjs = dayjs(lastEmployment.fromDate).subtract(1, 'month').endOf('month');

      this.unEmpForm.get('toDate').setValue(toDateDayjs.toDate());
      this.unEmpForm.get('isCurrentEmployment').setValue(false);

      this.unEmpForm.patchValue({
        toMonth: toDateDayjs.month(),
        toYear: toDateDayjs.year()
      });
      
    }

    if (isMissingIndex !== undefined && isMissingIndex !== null) {
      const nextEmp = this.employmentList[isMissingIndex + 1];
      const prevEmp = this.employmentList[isMissingIndex - 1];
      
      const prevFrom = gapPrevFromDate ?? prevEmp?.fromDate;
      const nextTo = gapNextToDate ?? nextEmp?.toDate;
      
      if (nextTo && prevFrom) {
        const toDateForGap = dayjs(prevFrom).subtract(1, 'month').endOf('month');
        const fromDateForGap = dayjs(nextTo).add(1, 'month').startOf('month');
                
        this.unEmpForm.patchValue({
          toMonth: toDateForGap.month(),
          toYear: toDateForGap.year(),
          fromMonth: fromDateForGap.month(),
          fromYear: fromDateForGap.year()
        });
        
      }
    }

    if (this.isModifyNewCurrentAddress) {
      // When user adds new current record, they should not be able to choose toDate month of previous record
      if (this.employmentList && this.employmentList.length > 0) {
        const baseDate = new Date(
          this.currentNewUnEmployment?.isCurrentEmployment
            ? this.employmentList[0].fromDate
            : this.employmentList[0].toDate
        );
        baseDate.setMonth(baseDate.getMonth() + 1);
        baseDate.setDate(1);
        this.minFromDate = baseDate;
      }
    }

    // Initialize dropdown options
    this.updateDropdownOptions();
  }

  //here element will be used to detect the datepicker control
  private initializeDates() {
    this.currentDate = new Date();
    this.currentDate.setMonth(this.currentDate.getMonth() + 1, 0);
    this.lastDate = new Date(this.currentDate);
    this.lastDate.setFullYear(this.lastDate.getFullYear() - 10);
    this.maxToDate = new Date(this.currentDate);
  }

  onUpdateEmploymentPressed($event): void {
    if ($event) {
      this.maxToDate = new Date(this.currentDate);
      this.unEmpForm = this.createUnEmploymentForm();

      this.populateUnemployedForm($event);

      if ($event?.isCurrentEmployment) {
        this.editFormTitle = 'Are you currently not working?';
      } else {
        this.editFormTitle = 'Were you not working during this time?';
      }
    }
  }

  onCancel(): void {
    if (this.isModifyNewCurrentAddress) {
      this.registrationService.updateEmploymentHistoryStore([]);
      this.router.navigateByUrl(`/${ROUTE_EMPLOYMENT_HISTORY}`);
      return;
    }
    this.location.back();
  }

  onLog() {
  }

  goBack(): void {
    this.location.back();
  }

  onSubmit(form: UntypedFormGroup): void {
    this.isContentReady = false;
    if (!form.valid) {
      return;
    }
    
    let model = null;

    model = this.prepareUnEmploymentModel();
    const modelWithId = { ...model };

    if (model) {
      const excludeIds: string[] = [];
      if (modelWithId.id) {
        excludeIds.push(modelWithId.id);
      }
      if (
        this.currentNewUnEmployment?.isCurrentEmployment &&
        this.isModifyNewCurrentAddress &&
        model.isCurrentEmployment &&
        this.currentNewUnEmployment?.id
      ) {
        excludeIds.push(this.currentNewUnEmployment.id);
      }
    }

    if (model && model.id) {
      let id = model.id;
      delete model.id;
      if (model.isCurrentEmployment) {
        delete model.toDate;
      }
      this.subs.add(
        this.registrationService.updateEmployment(model, id).subscribe(
          () => {
            const updatedEmp = this.employmentList.findIndex(emp => emp.id === modelWithId.id);
            this.employmentList[updatedEmp] = modelWithId;
            this.registrationService.updateEmploymentHistoryStore([...this.employmentList]);
            this.unEmpForm.reset();
            this.onCancel();
          },
          (error: any) => {
            this.toastService.showError(error?.message);
            this.isContentReady = true;
            this.unEmpForm.reset();
          }
        )
      );
    } else if (model) {
      if (this.currentNewUnEmployment?.isCurrentEmployment && this.isModifyNewCurrentAddress && model.isCurrentEmployment) {
        this.currentNewUnEmployment.isCurrentEmployment = false;
        this.currentNewUnEmployment.toDate = dayjs(this.currentDate).endOf('month').format('YYYY-MM-DD');
        const id = this.currentNewUnEmployment.id;
        delete this.currentNewUnEmployment.id;
        delete this.currentNewUnEmployment.modifiedDate;
        delete this.currentNewUnEmployment.creationDate;
        delete this.currentNewUnEmployment.version;
        delete this.currentNewUnEmployment.reasonForLeaving;
        this.updateExistingAndCreateNewEmployment(this.currentNewUnEmployment, id, model);
      } else {
        this.onEmploymentAdded(model);
      }
    } else {
      this.onCancel();
    }
  }

  private updateExistingAndCreateNewEmployment(model: any, id: string, newCurrentEmployment?: Unemployment): void {
    if (model?.employerAddress) {
      const countryMatch = model?.employerAddress?.match(/, ([A-Za-z\s]+)$/);
      model['employerCountry'] = countryMatch ? countryMatch[1].trim() : null;
    }
    Object.keys(model).forEach(key => {
      if (model[key] === null || model[key] === '') {
        delete model[key];
      }
    });
    this.isContentReady = false;
    this.registrationService
      .updateEmployment(model, id)
      .pipe(
        catchError((error: any) => {
          this.isContentReady = true;
          this.toastService.showError(error?.message);
          return of(false);
        })
      )
      .subscribe(res => {
        if (res) {
          this.errorMessage = undefined;
          if (this.currentNewUnEmployment) {
            const modelToSave = { ...model, id: id };
            const updatedEmp = this.employmentList.findIndex(emp => emp.id === id);
            this.employmentList[updatedEmp] = modelToSave;
            this.registrationService.updateEmploymentHistoryStore([...this.employmentList]);
            this.storageService.store('currentEmploymentState', null);
            this.onEmploymentAdded(newCurrentEmployment);
          }
        }
      });
  }

  get unEmpFromDate() {
    return this.unEmpForm.get('fromDate');
  }
  get unEmpToDate() {
    return this.unEmpForm.get('toDate');
  }
  get isCurrentUnemployment() {
    return this.unEmpForm.get('isCurrentEmployment');
  }

  getunEmpFromDateErrorMessage() {
    return this.unEmpFromDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getunEmpToDateErrorMessage() {
    return this.unEmpToDate.errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  onDatePickerClick(datepicker: any) {
    datepicker.open();
  }

  private createUnEmploymentForm(): UntypedFormGroup {
    const form = this.fb.group({
      fromDate: ['', [Validators.required, this.checkFromDateValidation.bind(this)]],
      toDate: ['', [Validators.required, this.checkToDateValidation.bind(this)]],
      // New month/year controls for FROM date
      fromMonth: ['', Validators.required],
      fromYear: ['', Validators.required],
      // New month/year controls for TO date
      toMonth: ['', Validators.required],
      toYear: ['', Validators.required],
      reason: [''],
      id: [''],
      isCurrentEmployment: ['']
    });

    // Keep date fields in sync as user selects month/year
    form.get('fromMonth')?.valueChanges.subscribe((month) => {
      if (month === null || month === '') {
        form.patchValue({ fromDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('fromYear')?.valueChanges.subscribe((year) => {
      if (year === null || year === '') {
        form.patchValue({ fromDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('toMonth')?.valueChanges.subscribe((month) => {
      if (month === null || month === '') {
        form.patchValue({ toDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });
    form.get('toYear')?.valueChanges.subscribe((year) => {
      if (year === null || year === '') {
        form.patchValue({ toDate: null }, { emitEvent: false });
      }
      this.updateDateFieldsFromMonthYear();
      this.updateDropdownOptions(); // Recalculate available options
      this.errorMessage = undefined; // Clear error when dates change
      this.checkOverlapWarning();
    });

    return form;
  }

  private checkOverlapWarning(): void {
    const fromDate = this.unEmpForm.get('fromDate')?.value;
    const toDate = this.unEmpForm.get('toDate')?.value;
    if (!fromDate) {
      this.overlapWarningMessage = undefined;
      return;
    }
    const candidate = {
      fromDate: fromDate,
      toDate: toDate || null,
      id: this.unEmpForm.get('id')?.value || null,
      employmentState: EMPLOYMENT_STATE_UNEMPLOYED
    } as any;
    const excludeIds = candidate.id ? [candidate.id] : [];
    const hasOverlap = hasOppositeStateDateOverlap(
      candidate,
      EMPLOYMENT_STATE_UNEMPLOYED,
      this.employmentList,
      this.currentDate,
      excludeIds
    );
    this.overlapWarningMessage = hasOverlap ? CROSS_STATE_OVERLAP_WARNING_MESSAGE : undefined;
  }

  private checkFromDateValidation(control: AbstractControl): Promise<any> {
    if (control && control?.parent) {
      let fromDate = control?.value;
      const toDate = control?.parent?.get('toDate').value || this.currentDate;
      if (fromDate && toDate && new Date(fromDate).getTime() >= new Date(toDate).getTime()) {
        return Promise.resolve({
          maxDateError: true
        });
      }
    }
    return null;
  }

  private checkToDateValidation(control: AbstractControl): Promise<any> {
    if (control && control?.parent) {
      let toDate = control?.value;
      const fromDate = control?.parent?.get('fromDate').value;
      if (fromDate && toDate && new Date(toDate).getTime() <= new Date(fromDate).getTime()) {
        return Promise.resolve({
          minDateError: true
        });
      }
      if (toDate) {
        const t = dayjs(toDate);
        const n = dayjs();
        if (t.year() > n.year() || (t.year() === n.year() && t.month() > n.month())) {
          return Promise.resolve({
            futureToDateError: true
          });
        }
      }
    }
    return null;
  }

  private prepareUnEmploymentModel(): Unemployment {
    const formModel = this.unEmpForm.value;
    const trimString = (value: any) => (typeof value === 'string' ? value.trim() : value);
  
    const model = {
      id: formModel.id as string,
      fromDate: this.datePipe.transform(formModel.fromDate, 'yyyy-MM-dd') as string,
      toDate: this.datePipe.transform(formModel.toDate, 'yyyy-MM-dd') as string,
      reasonForLeaving: formModel.reason as string,
      employmentState: EMPLOYMENT_STATE_UNEMPLOYED,
      isCurrentEmployment: formModel?.isCurrentEmployment ? formModel.isCurrentEmployment : ''
    } as Unemployment;
  
    if (this.isFirstRecord || formModel?.isCurrentEmployment) {
      model.isCurrentEmployment = true;
      delete model.toDate;
    }
    if (!model.reasonForLeaving) {
      delete model.reasonForLeaving;
    }
    Object.keys(model).forEach(key => {
      if (model[key] === null || model[key] === '') {
        delete model[key];
      }
    });

    return model;
  }

  private populateUnemployedForm(unemployed: Unemployment): void {
    this.isUpdating = true;
    
    // Populate month/year dropdowns from the date values FIRST
    this.populateMonthYearFromDate(unemployed);
    
    // Explicitly clear the date fields - they will be recalculated from month/year
    // Don't set fromDate/toDate directly - they will be calculated from month/year
    this.unEmpForm.patchValue({
      id: unemployed.id,
      fromDate: null,  // Clear old date - will be recalculated
      toDate: null,    // Clear old date - will be recalculated
      reason: unemployed.reasonForLeaving ? unemployed.reasonForLeaving : '',
      isCurrentEmployment: unemployed?.isCurrentEmployment
    });
    
    // Trigger date calculation from the month/year values
    this.updateDateFieldsFromMonthYear();
    
    // Update dropdown options after form is populated
    this.updateDropdownOptions();
    
    // Ensure TO month/year fields are enabled when editing (they might be disabled when adding new)
    // Only enable if it's a previous unemployment being edited (not current unemployment)
    if (unemployed.id && !unemployed.isCurrentEmployment) {
      this.unEmpForm.get('toMonth')?.enable();
      this.unEmpForm.get('toYear')?.enable();
    }
  }

  private onEmploymentAdded(model: Unemployment) {
    // TODO: Remove this logic when API ignores these fields.
    model.id = undefined;
    if (this.isModifyNewCurrentAddress) {
      model.isCurrentEmployment = true;
      delete model.toDate;
    }
    this.isContentReady = false;
    this.subs.add(
      this.registrationService
        .saveEmployment(model)
        .pipe(
          catchError((error: any) => {
            this.isContentReady = true;
            this.toastService.showError(error?.message);
            return of(false);
          })
        )
        .subscribe(response => {
          if (response?.data) {
            const empRecords = [...this.employmentList];
            empRecords.push(response?.data);
            const recordsToSave = [...empRecords];
            this.registrationService.updateEmploymentHistoryStore(recordsToSave);
          }
          this.onCancel();
        })
    );
  }

  private loadExperience(): void {
    this.isContentReady = false;
    this.registrationService.getEmploymentHistory().subscribe(response => {
      this.employmentList = [...response];
      if (this.employmentList) {
        if (this.employmentList?.length) {
          this.employmentList.sort((a, b) => compareEmploymentByToDateDesc(a, b, this.currentDate));

          // Check if there's already a current employment
          const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);
          this.isFirstRecord = !hasCurrentEmployment;
          this.checkMissingGap();
        } else {
          this.isGreaterThan10years = false;
          this.isFirstRecord = true;
        }
        this.isContentReady = true;

        // Handle query parameters
        this.activatedRoute.queryParams.subscribe(params => {
          const hasMissingIndex = params.missingIndex !== undefined && params.missingIndex !== null;
          const missingIndex = hasMissingIndex ? parseInt(params.missingIndex, 10) : undefined;
          const gapPrevFromDate = params.gapPrevFromDate;
          const gapNextToDate = params.gapNextToDate;

          // If newCurrentAddress is specified, this means we're adding a new current
          // unemployment to replace an existing one
          if (params.newCurrentAddress) {
            this.isModifyNewCurrentAddress = true;

            // Store the current employment to be updated when saving
            const currentEmployment = this.employmentList.find(emp => emp && emp.isCurrentEmployment);
            if (currentEmployment) {
              this.currentNewUnEmployment = currentEmployment;
              this.storageService.store('currentEmploymentState', currentEmployment);
            }
          } else {
            this.isModifyNewCurrentAddress = false;
            this.currentNewUnEmployment = null;
            this.storageService.store('currentEmploymentState', null);
          }
          // If no current employment exists and this isn't a gap fill
          if (
            !params.newCurrentAddress &&
            !this.isFirstRecord &&
            (params.missingIndex === undefined || params.missingIndex === null)
          ) {
            const hasCurrentEmployment = this.employmentList.some(emp => emp && emp.isCurrentEmployment);

            if (!hasCurrentEmployment) {
              // Block adding previous unemployment without current employment
              this.toastService.showError('You need to add your current employment first.');
              this.location.back();
              return;
            }
          }

          this.onAddUnemployment(missingIndex, gapPrevFromDate, gapNextToDate);
        });

        // Handle route parameters for editing existing records
        this.activatedRoute.params.subscribe(params => {
          if (params.id) {
            let empEvent = this.employmentList.find(el => el.id === params.id);
            if (empEvent) {
              this.onUpdateEmploymentPressed(empEvent);
            }
          }
        });
      } else {
        this.location.back();
      }
    });
  }

  private checkMissingGap(): void {
    let isYearsInOrder = true;
    let employmentData: any[] = [...this.employmentList];
    const currentEmployment = this.employmentList.filter(e => e.isCurrentEmployment);
    let currentDifferenceMonth = 0;
    const currentEmpToDate = new Date(this.currentDate);
    const currentEmpFromDate = new Date(currentEmployment[0]?.fromDate);
    currentDifferenceMonth = Math.round((currentEmpToDate.getTime() - currentEmpFromDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

    if (this.employmentList.length > 1) {
      let spliceIndex = 0;
      for (let index = 0; index < employmentData.length - 1; index++) {
        if (employmentData[index + 1]) {
          const date2CompareToDate = new Date(employmentData[index + 1].toDate);
          const date2CompareFromDate = new Date(employmentData[index].fromDate);
          const monthDiff = Math.round((date2CompareToDate.getTime() - date2CompareFromDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
          if (monthDiff === 0) {
            const currToDate = new Date(employmentData[index + 1].toDate);
            const currFromDate = new Date(employmentData[index + 1].fromDate);

            currentDifferenceMonth += Math.round((currToDate.getTime() - currFromDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

            if (this.checkEmploymentHasTenYearsInfo(currentDifferenceMonth, isYearsInOrder)) {
              return;
            }
          } else {
            if (!spliceIndex) {
              spliceIndex = index + 1;
            } else {
              spliceIndex += index;
            }
            if (employmentData.length - 1 === index + 1 && employmentData.length - 2 === index) {
              this.employmentList.splice(this.employmentList.length - 1, 0, { fromDate: null } as Employment);
            } else {
              this.employmentList.splice(spliceIndex, 0, { fromDate: null } as Employment);
            }
            if (index === 0) {
              isYearsInOrder = this.checkIsYearsInOrder(currentDifferenceMonth);
            } else {
              isYearsInOrder = this.checkIsYearsInOrder(currentDifferenceMonth);
            }
          }
        }
      }
    } else {
      this.checkEmploymentHasTenYearsInfo(currentDifferenceMonth, isYearsInOrder);
    }
  }

  private checkEmploymentHasTenYearsInfo(month: number, isYearsInOrder: boolean): boolean {
    if (month >= 120 && isYearsInOrder) {
      this.isGreaterThan10years = true;
      return true;
    } else {
      this.isGreaterThan10years = false;
      return false;
    }
  }

  private checkIsYearsInOrder(currentDiffAddressMonth): boolean {
    if (currentDiffAddressMonth < 120) {
      return false;
    } else {
      return true;
    }
  }

  // Helper method to convert month/year to date
  private updateDateFieldsFromMonthYear(): void {
    const fromMonth = this.unEmpForm.get('fromMonth')?.value;
    const fromYear = this.unEmpForm.get('fromYear')?.value;
    const toMonth = this.unEmpForm.get('toMonth')?.value;
    const toYear = this.unEmpForm.get('toYear')?.value;

    // Update FROM date - use start of month (day 1)
    // Only update if BOTH month and year are set
    if ((fromMonth === 0 || fromMonth) && fromMonth !== null && fromMonth !== '' && fromYear) {
      const fromDate = new Date(fromYear, fromMonth, 1); // Day 1 of the selected month
      this.unEmpForm.patchValue({ fromDate: fromDate }, { emitEvent: false });
    }

    // Update TO date - use end of month (last day)
    // Only update if BOTH month and year are set
    if ((toMonth === 0 || toMonth) && toMonth !== null && toMonth !== '' && toYear) {
      const toDate = new Date(toYear, toMonth + 1, 0); // Last day of the selected month
      this.unEmpForm.patchValue({ toDate: toDate }, { emitEvent: false });
    }

    this.unEmpForm.get('fromDate')?.updateValueAndValidity({ emitEvent: false });
    this.unEmpForm.get('toDate')?.updateValueAndValidity({ emitEvent: false });
  }

  // Helper method to populate month/year dropdowns from existing date
  private populateMonthYearFromDate(unemployment: Unemployment): void {
    if (unemployment.fromDate) {
      const fromDate = new Date(unemployment.fromDate);
      this.unEmpForm.patchValue({
        fromMonth: fromDate.getMonth(),
        fromYear: fromDate.getFullYear()
      });
    }

    if (unemployment.isCurrentEmployment) {
      // For current unemployment, always use current month/year for TO date
      const currentMonth = this.currentDate.getMonth();
      const currentYear = this.currentDate.getFullYear();
      this.unEmpForm.patchValue({
        toMonth: currentMonth,
        toYear: currentYear
      });
      // Clear validators for current unemployment
      this.unEmpForm.get('toMonth')?.clearValidators();
      this.unEmpForm.get('toYear')?.clearValidators();
      this.unEmpForm.get('toMonth')?.updateValueAndValidity();
      this.unEmpForm.get('toYear')?.updateValueAndValidity();
    } else if (unemployment.toDate) {
      const toDate = new Date(unemployment.toDate);
      this.unEmpForm.patchValue({
        toMonth: toDate.getMonth(),
        toYear: toDate.getFullYear()
      });
    }
  }

  // Get the current month name
  getCurrentMonthName(): string {
    const currentMonth = this.currentDate.getMonth();
    return this.months[currentMonth]?.name || '';
  }

  // Get the current year
  getCurrentYear(): number {
    return this.currentDate.getFullYear();
  }

  // Get available months for FROM month dropdown
  getAvailableFromMonths(): any[] {
    return this.availableFromMonths;
  }

  // Get available years for FROM year dropdown
  getAvailableFromYears(): number[] {
    return this.availableFromYears;
  }

  // Get available months for TO month dropdown based on FROM date selection
  getAvailableToMonths(): any[] {
    return this.availableToMonths;
  }

  // Get available years for TO year dropdown
  getAvailableToYears(): number[] {
    return this.availableToYears;
  }

  private updateDropdownOptions(): void {
    const isCurrentEmployment = this.unEmpForm.get('isCurrentEmployment')?.value;
    const editingEmploymentId = this.unEmpForm.get('id')?.value;
    const fromYear = this.unEmpForm.get('fromYear')?.value;
    const fromMonth = this.unEmpForm.get('fromMonth')?.value;
    const toYear = this.unEmpForm.get('toYear')?.value;

    let availableYears = [...this.years];

    if (!isCurrentEmployment && !editingEmploymentId && this.minFromDate && this.maxToDate) {
      const minYear = dayjs(this.minFromDate).year();
      const maxYear = dayjs(this.maxToDate).year();
      availableYears = availableYears.filter(year => year >= minYear && year <= maxYear);
    }

    this.availableFromYears = availableYears;

    // Calculate available FROM months
    if (!fromYear) {
      this.availableFromMonths = [...this.months];
    } else {
      const currentYear = this.currentDate.getFullYear();
      const currentMonth = this.currentDate.getMonth();
      let availableMonths = [...this.months];

      // Do not cap by other employments (concurrent periods). Optional: no future months in current calendar year.
      if (fromYear === currentYear) {
        availableMonths = availableMonths.filter(m => m.value <= currentMonth);
      }

      this.availableFromMonths = availableMonths;
    }

    // Calculate available TO years (never after current calendar year)
    let availableToYears = [...this.years];
    const todayY = new Date().getFullYear();

    if (fromYear) {
      availableToYears = availableToYears.filter(y => y >= fromYear);
    }

    if (this.minFromDate && this.maxToDate) {
      const minYear = fromYear || dayjs(this.minFromDate).year();
      const maxYear = Math.min(dayjs(this.maxToDate).year(), todayY);
      availableToYears = availableToYears.filter(year => year >= minYear && year <= maxYear);
    } else {
      availableToYears = availableToYears.filter(y => y <= todayY);
    }

    this.availableToYears = availableToYears;

    // Calculate available TO months
    if (!toYear) {
      this.availableToMonths = [...this.months];
    } else {
      const now = new Date();
      let availableMonths = [...this.months];

      if (toYear === now.getFullYear()) {
        availableMonths = availableMonths.filter(m => m.value <= now.getMonth());
      }

      if (toYear === fromYear && fromMonth !== null && fromMonth !== '') {
        availableMonths = availableMonths.filter(m => m.value >= fromMonth);
      }

      this.availableToMonths = availableMonths;
    }

  }

  private scrollToDatePicker(): void {
    if (this.datePickerSection) {
      this.datePickerSection.nativeElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

}
